import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement
} from 'chart.js';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FaBell, FaCheckCircle } from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement
);

const API_URL = 'https://api.sentiguard.ai/api/get-device-data';

function AnimatedMarker({ position }) {
  const markerRef = React.useRef();
  const map = useMap();
  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLatLng(position);
      map.panTo(position, { animate: true, duration: 1 });
    }
  }, [position, map]);
  return <Marker ref={markerRef} position={position} />;
}

function getGsmSignalText(value) {
  if (value === null || value === undefined) return '-';
  if (value <= -110) return 'Foarte slab';
  if (value <= -100) return 'Slab';
  if (value <= -85) return 'Bun';
  return 'Excelent';
}

function BatteryBar({ level }) {
  if (level === null || level === undefined) return <div style={{height: 16, background: '#eee', borderRadius: 8, width: 80}} />;
  let color = '#27ae60';
  if (level < 20) color = '#e74c3c';
  else if (level < 50) color = '#f39c12';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 80,
        height: 16,
        background: '#eee',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #ccc',
        position: 'relative'
      }}>
        <div style={{
          width: `${level}%`,
          height: '100%',
          background: color,
          transition: 'width 0.3s'
        }} />
      </div>
      <span style={{ fontWeight: 'bold', color, fontSize: 18 }}>{level}%</span>
    </div>
  );
}

function DashboardComponent() {
  const [data, setData] = useState([]);
  const dashboardRef = useRef();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(API_URL);
        const json = await res.json();
        if (Array.isArray(json) && json.length > 0) {
          setData(json);
        } else {
          setData([]);
        }
      } catch (e) {
        setData([]);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, []);

  // Sortez datele crescător după timestamp (created_at)
  const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const last = sortedData.length > 0 ? sortedData[sortedData.length - 1] : null;

  const chartData = {
    microphone: {
      labels: sortedData.map((d, i) => `${i + 1}s`),
      datasets: [{
        label: 'Microphone dB',
        data: sortedData.map(d => d.microphone_db),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#e74c3c' : 'rgba(255,99,132,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
      }],
    },
    acceleration: {
      labels: sortedData.map((d, i) => `${i + 1}s`),
      datasets: [{
        label: 'Acceleration X',
        data: sortedData.map(d => d.motion_sensors?.acceleration_x),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#3498db' : 'rgba(54,162,235,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
      }],
    },
    battery: {
      labels: sortedData.map((d, i) => `${i + 1}s`),
      datasets: [{
        label: 'Battery %',
        data: sortedData.map(d => d.battery_level),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#27ae60' : 'rgba(75,192,192,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
      }],
    },
    gsm: {
      labels: sortedData.map((d, i) => `${i + 1}s`),
      datasets: [{
        label: 'GSM Signal dBm',
        data: sortedData.map(d => d.gsm_signal),
        borderColor: 'rgb(255, 205, 86)',
        backgroundColor: 'rgba(255, 205, 86, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#f39c12' : 'rgba(255,205,86,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
      }],
    }
  };

  const getChartOptions = (label) => ({
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
      annotation: {
        annotations: {
          lastValueLine: {
            type: 'line',
            xMin: sortedData.length - 1,
            xMax: sortedData.length - 1,
            borderColor: '#222',
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              display: true,
              content: 'Last value',
              position: 'start',
              color: '#222',
              font: { weight: 'bold' }
            }
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
      },
    },
  });

  const submenuOptions = ['1s', '5s', '1m', '5m'];

  const Section = ({ title, value, chartData, color = '#333' }) => (
    <div style={{ 
      marginBottom: 30, 
      padding: 20, 
      border: '1px solid #ddd', 
      borderRadius: 8,
      backgroundColor: '#f9f9f9'
    }}>
      <div style={{ 
        fontWeight: 'bold', 
        fontSize: 32, 
        marginBottom: 10,
        color: color,
        textTransform: 'uppercase',
        letterSpacing: 2
      }}>
        {title}
        <span 
          style={{ 
            fontWeight: 'bold', 
            fontSize: 44, 
            marginLeft: 16, 
            color: '#fff',
            background: color,
            borderRadius: 12,
            padding: '2px 18px',
            animation: 'pulse 1s infinite',
            verticalAlign: 'middle',
            display: 'inline-block',
            boxShadow: `0 0 10px ${color}55`
          }}
        >
          {value}
        </span>
      </div>
      <hr style={{ border: 'none', borderTop: '2px solid #eee', margin: '8px 0 12px 0' }} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {submenuOptions.map(opt => (
          <button
            key={opt}
            style={{
              padding: '4px 14px',
              borderRadius: 16,
              border: '1px solid #bbb',
              background: '#fff',
              fontWeight: 'bold',
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 1px 2px #eee',
              transition: 'background 0.2s',
              position: 'relative'
            }}
            tabIndex={-1}
            title="Perioada de colectare este fixă (1s)"
          >
            {opt}
            <span style={{
              position: 'absolute',
              left: '50%',
              top: '-28px',
              transform: 'translateX(-50%)',
              background: '#222',
              color: '#fff',
              fontSize: 12,
              borderRadius: 6,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.2s',
              zIndex: 10
            }}>
              Perioada fixă (1s)
            </span>
          </button>
        ))}
      </div>
      <div style={{ height: 300 }}>
        <Line data={chartData} options={getChartOptions(title)} />
      </div>
    </div>
  );

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.15); opacity: 0.7; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleFullscreen = () => {
    const elem = dashboardRef.current;
    if (!isFullscreen) {
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const onFullScreenChange = () => {
      setIsFullscreen(!!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      ));
    };
    document.addEventListener('fullscreenchange', onFullScreenChange);
    document.addEventListener('webkitfullscreenchange', onFullScreenChange);
    document.addEventListener('mozfullscreenchange', onFullScreenChange);
    document.addEventListener('MSFullscreenChange', onFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullScreenChange);
      document.removeEventListener('mozfullscreenchange', onFullScreenChange);
      document.removeEventListener('MSFullscreenChange', onFullScreenChange);
    };
  }, []);

  const alarmActive = false;
  const alarmType = alarmActive ? 'ALARM: Microphone' : 'normal';
  const statusColor = alarmActive ? '#e74c3c' : '#27ae60';
  const statusText = alarmActive ? 'ALARM' : 'NORMAL';

  return (
    <div ref={dashboardRef} style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: '#f0f0f0',
      overflow: 'hidden'
    }}>
      <header style={{
        textAlign: 'center',
        fontSize: 32,
        fontWeight: 'bold',
        padding: 20,
        backgroundColor: '#2c3e50',
        color: 'white',
        flex: '0 0 auto',
        position: 'relative'
      }}>
        Sentinel Dashboard
        <button
          onClick={handleFullscreen}
          style={{
            position: 'absolute',
            right: 30,
            top: 20,
            background: isFullscreen ? '#e74c3c' : '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '8px 18px',
            fontSize: 18,
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px #0002',
            transition: 'background 0.2s',
            zIndex: 10
          }}
          title={isFullscreen ? 'Ieși din full screen' : 'Full screen'}
        >
          {isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
        </button>
      </header>
      <div style={{ display: 'flex', flex: 1, padding: 20 }}>
        <div style={{ flex: 1, marginRight: 20, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <Section 
            title="Microphone" 
            value={last ? `${last.microphone_db} dB` : '-'} 
            chartData={chartData.microphone}
            color="#e74c3c"
          />
          <Section 
            title="Acceleration" 
            value={last ? `X: ${last.motion_sensors?.acceleration_x}, Y: ${last.motion_sensors?.acceleration_y}, Z: ${last.motion_sensors?.acceleration_z}` : '-'} 
            chartData={chartData.acceleration}
            color="#3498db"
          />
          <Section 
            title={last ? `Battery Level (${last.battery_level}%)` : 'Battery Level'}
            value={last ? <BatteryBar level={last.battery_level} /> : '-'} 
            chartData={chartData.battery}
            color="#27ae60"
          />
          <Section 
            title="GSM Signal" 
            value={last ? `${last.gsm_signal} dBm (${getGsmSignalText(last.gsm_signal)})` : '-'} 
            chartData={chartData.gsm}
            color="#f39c12"
          />
        </div>
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden'
        }}>
          <div style={{
            marginBottom: 10,
            padding: '12px 18px',
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #ddd',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            fontSize: 20,
            fontWeight: 'bold',
            boxShadow: '0 1px 4px #0001'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaCheckCircle style={{ color: statusColor, fontSize: 28 }} />
              Status:
              <span style={{ color: statusColor, fontWeight: 'bold', marginLeft: 4 }}>{statusText}</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 24 }}>
              <FaBell style={{ color: alarmActive ? '#e74c3c' : '#bbb', fontSize: 24 }} />
              Alarms Events:
              <span style={{ color: alarmActive ? '#e74c3c' : '#222', fontWeight: 'bold', marginLeft: 4 }}>{alarmType}</span>
            </span>
          </div>
          <div style={{
            border: '2px solid #ddd',
            height: 120,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            fontSize: 18,
            color: '#666',
            marginBottom: 20
          }}>
            📹 Camera Feed
          </div>
          <div style={{ 
            border: '2px solid #ddd', 
            height: '50%',
            borderRadius: 8,
            backgroundColor: '#fff',
            padding: 10
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
              GPS: {last ? `Lat ${last.gps?.lat}, Lng ${last.gps?.lng}` : '-'}
            </div>
            {last && last.gps ? (
              <MapContainer center={[last.gps.lat, last.gps.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <AnimatedMarker position={[last.gps.lat, last.gps.lng]} />
              </MapContainer>
            ) : (
              <div style={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa'}}>No GPS data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardComponent; 